import {
  IconUser,
  IconInfoCircle,
  IconFileText,
  IconPhoto,
  IconCurrencyReal,
  IconBook,
  IconMessageCircle,
  IconEye,
} from "@tabler/icons-react";

export const FORM_CONSTANTS = {
  MIN_PRICE: 250, // Mínimo R$ 250,00 conforme API
  MAX_DESCRIPTION_LENGTH: 200,
  AUTO_SAVE_DELAY: 2000,
  MAX_BANNER_SIZE_MB: 3, // Máximo 3MB conforme API
  MAX_GALLERY_SIZE_MB: 3, // Máximo 3MB conforme API
  MAX_GALLERY_IMAGES: 10, // Máximo 10 imagens conforme API
  BANNER_RECOMMENDED_WIDTH: 1200,
  BANNER_RECOMMENDED_HEIGHT: 400,
  SUCCESS_TIMER: 3000,
  CONFIRMATION_TIMER: 2000,
};

export const TOTAL_STEPS = 7;

export const STEPS_CONFIG = [
  { 
    title: "Agente & Precificação", 
    icon: IconUser, 
    description: "Selecione o agente, defina preços e descontos" 
  },
  { 
    title: "Categoria", 
    icon: IconInfoCircle, 
    description: "Categoria do agente" 
  },
  { 
    title: "Conteúdo", 
    icon: IconFileText, 
    description: "Descrição detalhada e tags" 
  },
  { 
    title: "Mídia", 
    icon: IconPhoto, 
    description: "Banner e galeria de imagens" 
  },
  { 
    title: "Instruções", 
    icon: IconBook, 
    description: "Instruções de uso e suporte" 
  },
  { 
    title: "Playground", 
    icon: IconMessageCircle, 
    description: "Teste interativo do agente" 
  },
  { 
    title: "Revisão", 
    icon: IconEye, 
    description: "Conferir informações finais" 
  },
];

export const CONTACT_TYPES = [
  { value: "email", label: "Email", icon: "la-envelope" },
  { value: "whatsapp", label: "WhatsApp", icon: "la-phone" },
];

export const CREDENTIAL_TYPES = [
  { value: "email", label: "E-mail" },
];

export const DEFAULT_FORM_VALUES = {
  agent: null,
  agent_name: "",
  agent_job: "",
  price: "250.00",
  description: "",
  video_url: "",
  banner: null,
  gallery_images: [],
  categories: [],
  tags: [],
  capabilities: [],
  features: [],
  detailed_content: {
    overview: "",
    features_description: "",
    technical_specs: "",
  },
  technical_specs: {},
  pricing_info: {
    original_price: "",
    discount_percentage: "",
    trial_period_days: "",
  },
  platform_name: "",
  platform_url: "",
  platform_description: "",
  credential_type: "",
  access_instructions: "",
  additional_info: "",
  configuration_steps: "",
  customization_options: "",
  setup_requirements: "",
  video_tutorial_url: "",
  video_platform: "",
  video_description: "",
  document_url: "",
  document_type: "",
  document_description: "",
  contact_type: "",
  contact_value: "",
  contact_name: "",
  support_hours: "",
  playground_url: "",
};

export const VALIDATION_MESSAGES = {
  REQUIRED_FIELDS: "Por favor, preencha todos os campos obrigatórios antes de continuar.",
  DRAFT_NOT_AVAILABLE: "Salvar rascunho não está disponível no modo de edição.",
  DRAFT_SAVED: "Rascunho salvo!",
  DRAFT_SAVED_DESCRIPTION: "Você pode continuar depois de onde parou.",
  DRAFT_ERROR: "Erro ao salvar rascunho",
  DRAFT_ERROR_DESCRIPTION: "Não foi possível salvar o rascunho. Tente novamente.",
  DRAFT_FOUND: "Rascunho encontrado!",
  DRAFT_FOUND_QUESTION: "Deseja continuar de onde parou?",
  CONFIRM_YES: "Sim, continuar",
  CONFIRM_NO: "Começar do zero",
  CANCEL_CONFIRMATION: "Tem certeza?",
  CANCEL_WARNING: "Você perderá todas as alterações não salvas.",
  CANCEL_YES: "Sim, sair",
  CANCEL_NO: "Continuar editando",
  HTTPS_WARNING: "É necessário que a URL comece com https://",
  PUBLICATION_CREATED: "Publicação Criada",
  PUBLICATION_UPDATED: "Publicação Atualizada",
  PUBLICATION_CREATED_DESCRIPTION: "A publicação foi criada e está aguardando aprovação.",
  PUBLICATION_UPDATED_DESCRIPTION: "A publicação foi atualizada com sucesso.",
  ERROR_CREATING: "Erro ao criar publicação.",
  ERROR_UPDATING: "Erro ao atualizar publicação.",
  ERROR_LOADING: "Erro ao carregar dados da publicação. Tente novamente.",
  LOADING_PUBLICATION: "Carregando dados da publicação...",
};

export const FILE_VALIDATION = {
  // Tipos aceitos conforme documentação da API: PNG, JPG, JPEG, WEBP
  ALLOWED_IMAGE_TYPES: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
  MAX_FILE_SIZE_BYTES: FORM_CONSTANTS.MAX_BANNER_SIZE_MB * 1024 * 1024,
  
  validateImageFile: (file) => {
    const errors = [];
    
    if (!file) return errors;
    
    if (!FILE_VALIDATION.ALLOWED_IMAGE_TYPES.includes(file.type.toLowerCase())) {
      errors.push('Tipo de arquivo não permitido. Use PNG, JPG, JPEG ou WEBP.');
    }
    
    if (file.size > FILE_VALIDATION.MAX_FILE_SIZE_BYTES) {
      errors.push(`Arquivo muito grande. Máximo: ${FORM_CONSTANTS.MAX_BANNER_SIZE_MB}MB`);
    }
    
    return errors;
  },
};

export const URL_VALIDATION = {
  HTTPS_PATTERN: /^https:\/\/.+/,
  VIDEO_PLATFORMS: {
    YOUTUBE: /(?:youtube\.com|youtu\.be)/,
    VIMEO: /vimeo\.com/,
  },
  
  validatePlatformUrl: (url) => {
    if (!url || typeof url !== 'string') return true;
    return URL_VALIDATION.HTTPS_PATTERN.test(url.trim());
  },
  
  detectVideoPlatform: (url) => {
    if (!url) return "";
    if (URL_VALIDATION.VIDEO_PLATFORMS.YOUTUBE.test(url)) return "youtube";
    if (URL_VALIDATION.VIDEO_PLATFORMS.VIMEO.test(url)) return "vimeo";
    return "";
  },
  
  detectDocumentType: (url) => {
    if (!url) return "";
    const extension = url.split('.').pop()?.toLowerCase();
    if (['pdf', 'pptx', 'ppsx'].includes(extension)) return extension;
    return "";
  },
};

export const IFRAME_SECURITY = {
  SANDBOX: "allow-scripts allow-same-origin allow-forms",
  PLAYGROUND_HEIGHT: "500px",
  REVIEW_HEIGHT: "160px"
};